import React from "react";
import { Badge, Calendar, Card, Col, Row, Statistic, Tag } from "antd";
import { BellOutlined, ClockCircleOutlined, ShoppingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import locale from "antd/es/date-picker/locale/nl_NL";

dayjs.extend(isBetween);

const StoreOverview = () => {
  const { t } = useTranslation();
  const storeSchedule = useSelector((state) => state.firestoreReducer.ordered.storeSchedule);
  const orderCapacity = useSelector((state) => state.firestoreReducer.ordered.orderCapacity);
  const notifications = useSelector((state) => state.firestoreReducer.ordered.notifications);

  // Create maps for quick lookup
  const scheduleMap = {};
  if (storeSchedule) {
    storeSchedule.forEach((schedule) => {
      scheduleMap[schedule.date] = schedule;
    });
  }

  const capacityMap = {};
  if (orderCapacity) {
    orderCapacity.forEach((capacity) => {
      capacityMap[capacity.date] = capacity;
    });
  }

  // Check if a day is normally closed
  const isNormallyClosed = (date) => {
    const day = date.day();
    return day === 0 || day === 1 || day === 6;
  };

  // Get combined status for a date
  const getCombinedStatus = (date) => {
    const dateStr = date.format(DATE_FORMAT_DD_MM_YYYY);
    const schedule = scheduleMap[dateStr];
    const capacity = capacityMap[dateStr];
    const normallyClosed = isNormallyClosed(date);

    // Determine if store is open
    let isOpen = true;
    if (schedule) {
      isOpen = schedule.isOpen;
    } else if (normallyClosed) {
      isOpen = false;
    }

    if (!isOpen) {
      return {
        type: "error",
        text: t("day_closed"),
        color: "red",
        details: schedule?.reason || t("store_closed_this_day"),
      };
    }

    // Check capacity if store is open
    if (capacity && capacity.limits && capacity.limits.length > 0) {
      let isFull = false;
      let isAlmostFull = false;
      let totalCurrent = 0;
      let totalLimit = 0;

      capacity.limits.forEach((limit) => {
        totalCurrent += limit.currentAmount;
        totalLimit += limit.limit;
        const percentage = (limit.currentAmount / limit.limit) * 100;
        if (percentage >= 100) {
          isFull = true;
        } else if (percentage >= 80) {
          isAlmostFull = true;
        }
      });

      if (isFull) {
        return {
          type: "error",
          text: t("day_full"),
          color: "red",
          details: `${totalCurrent}/${totalLimit}`,
          capacity: capacity.limits,
        };
      }
      if (isAlmostFull) {
        return {
          type: "warning",
          text: t("almost_full"),
          color: "orange",
          details: `${totalCurrent}/${totalLimit}`,
          capacity: capacity.limits,
        };
      }
      return {
        type: "success",
        text: t("day_open"),
        color: "green",
        details: `${totalCurrent}/${totalLimit}`,
        capacity: capacity.limits,
      };
    }

    return {
      type: "success",
      text: t("day_open"),
      color: "green",
      details: schedule?.reason || null,
    };
  };

  // Render calendar cell
  const dateCellRender = (date) => {
    const status = getCombinedStatus(date);

    return (
      <div style={{ minHeight: 60, padding: 4 }}>
        <Badge status={status.type} text={status.text} />
        {status.details && <div style={{ fontSize: 10, marginTop: 4, color: "#666" }}>{status.details}</div>}
        {status.capacity && (
          <div style={{ fontSize: 9, marginTop: 2 }}>
            {status.capacity.map((limit, idx) => (
              <div key={idx}>
                <Tag
                  color={
                    limit.currentAmount >= limit.limit
                      ? "red"
                      : limit.currentAmount / limit.limit >= 0.8
                        ? "orange"
                        : "blue"
                  }
                  style={{ fontSize: 8, padding: "0 2px", marginTop: 2 }}
                >
                  {t(limit.deliveryMethod === "pickup" ? "pickup" : "send")} {t(limit.scope)}: {limit.currentAmount}/
                  {limit.limit}
                </Tag>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Calculate statistics
  const getActiveNotificationsCount = () => {
    if (!notifications) return 0;
    const now = dayjs();
    return notifications.filter((n) => {
      const start = dayjs.unix(n.startDateTime);
      const end = dayjs.unix(n.endDateTime);
      return now.isBetween(start, end);
    }).length;
  };

  const getScheduleChangesCount = () => {
    return storeSchedule ? storeSchedule.length : 0;
  };

  const getCapacityLimitsCount = () => {
    return orderCapacity ? orderCapacity.length : 0;
  };

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t("no_schedule_changes")}
              value={getScheduleChangesCount()}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t("no_capacity_limits")}
              value={getCapacityLimitsCount()}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t("active_notifications_count")}
              value={getActiveNotificationsCount()}
              prefix={<BellOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
      </Row>

      <Card title={t("store_overview")}>
        <Calendar dateCellRender={dateCellRender} locale={locale} />
      </Card>
    </div>
  );
};

export default StoreOverview;
